import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '.';
import { SimilarCardSlim, TaskKind, TrainingCard, TrainingCardData, TrainingType } from '../types/TrainingTypes';
import { trainingActionCreators } from './Training';

interface InterpretState {
    task: string | undefined,
    taskAux: string | undefined,
    taskForms: string | undefined,

    solution: string | undefined,
    maskedSolution: string | undefined,

    solutionAux: string | undefined,
    maskedSolutionAux: string | undefined,

    solutionForms: string | undefined,
    maskedSolutionForms: string | undefined,

    example: string | undefined,
    isExampleShown: boolean,
    similar: SimilarCardSlim[] | undefined,
    symbolsShownCount: number,
}

const initialState: InterpretState = {
    task: '',
    taskAux: '',
    taskForms: '',

    solution: '',
    maskedSolution: '',
    solutionAux: '',
    solutionForms: '',
    maskedSolutionAux: '',
    maskedSolutionForms: '',

    example: '',
    isExampleShown: false,

    similar: undefined,
    symbolsShownCount: 0,
}

const slice = createSlice({
    name: 'interpret',
    initialState,
    reducers: {
        setNextCard: (state, action: PayloadAction<TrainingCard>) => {
            const card = action.payload;
            state.symbolsShownCount = 0;
            state.isExampleShown = card.resultInfo.isWeak && card.resultInfo.reviews >= 50 && card.modeInfo.isDirect;
            updateCardParts(state, card, false);
            updateUserChoice(state, card, false)
        },

        showHint: (state, action: PayloadAction<{ card: TrainingCard, symbolsShownCount: number }>) => {
            const { card, symbolsShownCount } = action.payload;
            state.symbolsShownCount = symbolsShownCount;
            updateCardParts(state, card!, false);
            updateUserChoice(state, card!, false);
        },

        toggleExample: state => {
            if (!state.isExampleShown)
                state.isExampleShown = true;
            else if (state.taskForms)
                state.isExampleShown = false;
        },

        showSolution: (state, action: PayloadAction<TrainingCard>) => {
            const card = action.payload;
            state.isExampleShown = true;
            updateCardParts(state, card, true);
            updateUserChoice(state, card, true);
        },
    },

    extraReducers: (builder) => {
        builder.addCase('training/reset', (state, action) => {
            return initialState;
        });

        builder.addCase('training/refreshCardData', (state, action) => {
            const data = (action as PayloadAction<TrainingCardData>).payload;
            if (data.card?.trainingType === TrainingType.Interpret) {
                updateCardParts(state, data.card!, data.isSolutionShown);
                updateUserChoice(state, data.card!, data.isSolutionShown);
            }
        });
    }
});

function updateCardParts(state: InterpretState, card: TrainingCard, isSolutionShown: boolean) {
    const ts = getCardParts(card, isSolutionShown);
    state.task = ts.task;
    state.taskAux = ts.taskAux;
    state.taskForms = ts.taskForms;
    state.solution = ts.solution.replace(/;;/gi, '\r\n').replace(/\r\n;/gi, ';\r\n');
    state.solutionAux = ts.solutionAux?.replace(/;;/gi, '\r\n').replace(/\r\n;/gi, ';\r\n');
    state.solutionForms = ts.solutionForms?.replace(/;;/gi, '\r\n').replace(/\r\n;/gi, ';\r\n');
}

function updateUserChoice(state: InterpretState, card: TrainingCard, isSolutionShown: boolean) {
    const { solution, solutionAux, solutionForms } = state;
    if (isSolutionShown)
        state.symbolsShownCount = solution!.length;

    state.maskedSolution = state.symbolsShownCount > 0 ? solution!.substring(0, state.symbolsShownCount) + solution!.substring(state.symbolsShownCount).replace(/./gi, '_') : '';

    state.maskedSolutionAux = isSolutionShown ? solutionAux : '';
    state.maskedSolutionForms = isSolutionShown ? solutionForms : '';
    state.example = card?.example?.replace(/([;?!.]);/gi, '$1\r\n');
    state.similar = isSolutionShown ? card!.similar : undefined;
}

function getCardParts(card: TrainingCard, isSolutionShown: boolean) {
    const k = card.modeInfo.modeSetting.taskKind;
    const isCardDirect = card.modeInfo.isDirect;

    const isPhraseFirst = !card.pronunciation || k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhrasePronunciation || k === TaskKind.PhraseForms || k === TaskKind.Phrase;
    const isPronunciationFirst = card.pronunciation && (k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationPhrase || k === TaskKind.PronunciationForms || k === TaskKind.Pronunciation)

    const isPhraseSecond = card.pronunciation && (k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationPhrase);
    const isPronunciationSecond = card.pronunciation && (k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhrasePronunciation);

    const isFormsThird = card.forms && (k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhraseForms || k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationForms)

    const first = isPronunciationFirst ? card.pronunciation : card.phrase;
    const second = isPhraseSecond ? card.phrase : isPronunciationSecond ? card.pronunciation : '';
    const third = isFormsThird ? card.forms : '';

    return {
        task: isCardDirect ? first : card.meaning,
        taskAux: isCardDirect ? second : '',
        taskForms: isCardDirect ? third : '',
        solution: isCardDirect ? card.meaning : first,
        solutionAux: isCardDirect ? '' : second,
        solutionForms: isCardDirect ? '' : third,
    }
}

const setNextCard = (card: TrainingCard) => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(slice.actions.setNextCard(card));
    }
}

const showHint = () => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const state = getState().interpret;
        const training = getState().training;

        const ts = getCardParts(training.card!, false);
        const symbolsShownCount = getHintSymbolsShownCount(state.symbolsShownCount, ts.solution);
        if (symbolsShownCount === ts.solution.length)
            dispatch(showSolution());
        else {
            dispatch(slice.actions.showHint({
                card: training.card!,
                symbolsShownCount,
            }));
        }
    }
};

function getHintSymbolsShownCount(symbolsShownCount: number, solution: string) {
    symbolsShownCount++;

    if (symbolsShownCount < solution.length) {
        const match = /^[ .,?!:;[]()-]+/.exec(solution.substring(symbolsShownCount));
        if (match)
            symbolsShownCount += match[0].length;
    }
    else
        symbolsShownCount = solution.length;
    return symbolsShownCount;
}

const showSolution = () => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const training = getState().training;

        dispatch(trainingActionCreators.showSolution(false));
        dispatch(slice.actions.showSolution(training.card!));
    }
};

export const interpretReducer = slice.reducer;

export const interpretActionCreators = {
    setNextCard,
    showHint,
    toggleExample: slice.actions.toggleExample,
    showSolution,
}
